import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ai as AiGen } from '@valvur/shared-grpc';
import { AuthService } from '../auth/services/auth.service';
import { ParentFamilyContextService } from '../parent-family/parent-family-context.service';
import {
  KID_TOPIC_SYNC_NEUTRAL,
} from './constants/topic-category.constants';
import { resolveSendScope, topicsForKid } from './parent-ai-scope.util';

const BASE = `${environment.baseUrl}/ai`;
const QUERY_KEY_PREFIX = ['parent-ai'] as const;

/** Patterns that indicate the parent may create/assign a duty (align with ai-service detectKidDataQueryIntent). */
const DUTY_CREATE_PATTERNS = [
  /(add|create|assign|set|remind) .* (chore|duty|task|homework|assignment)/i,
  /(assign|remind|ask|tell) .+ to .+/i,
  /(can you|could you|please) (ask|remind|tell) .+ to .+/i,
];

function isDutyCreationIntent(message: string | undefined): boolean {
  if (!message?.trim()) return false;
  return DUTY_CREATE_PATTERNS.some((p) => p.test(message));
}

/** POST /conversation may include topic routing hints (proto fields 2–3). */
export type ParentAiConversationResponse = AiGen.Conversation;

@Injectable({ providedIn: 'root' })
export class ParentAiService {
  #http = inject(HttpClient);
  #queryClient = inject(QueryClient);
  #authService = inject(AuthService);
  #family = inject(ParentFamilyContextService);

  /** Set by component when user selects a topic to view its thread. */
  selectedTopicId = signal<string | null>(null);

  /** Shared family context (Patrol + Assistant). */
  selectedChildId = this.#family.selectedChildId;

  /** Set by component only after user has loaded: true when no kids are linked (verification mode). Default false so we don't run the query before kids are resolved. */
  isVerificationMode = signal(false);

  /** Set when selected child changes; cleared after topics are refetched and first topic is selected. */
  kidAwaitingTopicSync = signal<string | null>(null);

  /** Prevents the kid/topic sync effect from re-entering while a refetch is in flight. */
  topicSyncInFlight = signal(false);

  /** True after a child change when there is no existing topic thread (start fresh chat). */
  preferNewConversation = signal(false);

  /**
   * POST /conversation body shown until selectedTopicId is wired (new-thread flow).
   * Cleared once the topic thread cache is seeded.
   */
  pendingThreadConversation = signal<AiGen.Conversation | null>(null);

  /**
   * Last plan/quota gate code (`ai.kid_agent`, `ai.general.requests`, …).
   * Soft gates come from a successful POST; hard quotas from PAYMENTS_QUOTA_EXCEEDED.
   * Cleared when dismissed or starting a new conversation.
   */
  planGateCode = signal<string | null>(null);
  /** Optional limit from quota meta (for banner copy). */
  planGateLimit = signal<number | null>(null);

  constructor() {
    effect(() => {
      const awaitingKidId = this.kidAwaitingTopicSync();
      if (this.isVerificationMode() || !awaitingKidId) return;
      if (awaitingKidId === KID_TOPIC_SYNC_NEUTRAL) {
        if (this.selectedChildId() !== null) return;
      } else if (this.selectedChildId() !== awaitingKidId) {
        return;
      }
      if (this.topicSyncInFlight()) return;

      this.topicSyncInFlight.set(true);
      void this.syncAfterChildChange()
        .then((hasTopic) => {
          const awaiting = this.kidAwaitingTopicSync();
          if (awaiting === KID_TOPIC_SYNC_NEUTRAL) {
            if (this.selectedChildId() !== null) return;
          } else if (this.selectedChildId() !== awaiting) {
            return;
          }
          this.preferNewConversation.set(!hasTopic);
          this.kidAwaitingTopicSync.set(null);
        })
        .finally(() => {
          this.topicSyncInFlight.set(false);
        });
    });
  }

  // ─── Conversation (main thread) ───────────────────────────────────────────
  // Only used in verification mode (no kids linked). When kids are linked, conversation is loaded by topicId only.

  conversationQuery = injectQuery(() => {
    const verificationMode = this.isVerificationMode();
    return {
      queryKey: [...QUERY_KEY_PREFIX, 'conversation', verificationMode],
      queryFn: () =>
        lastValueFrom(
          this.#http.get<AiGen.Conversation>(`${BASE}/conversation`),
        ),
      retry: false,
      enabled: verificationMode,
    };
  });

  conversation = computed(
    () => this.conversationQuery.data() ?? { messages: [] },
  );
  conversationMessages = computed(() => this.conversation().messages ?? []);

  // ─── Conversation for a specific topic (filtered thread) ───────────────────
  // Used when kids are linked and a topic is selected (conversation by topicId; topic is scoped by kid).

  conversationForTopicQuery = injectQuery(() => {
    const topicId = this.selectedTopicId();
    return {
      queryKey: [...QUERY_KEY_PREFIX, 'conversation-for-topic', topicId],
      queryFn: () =>
        lastValueFrom(
          this.#http.get<AiGen.Conversation>(`${BASE}/conversation/for-topic`, {
            params: { topicId: topicId! },
          }),
        ),
      enabled: !!topicId,
      retry: false,
    };
  });

  /** Messages to display: verification mode → main conversation; has kids + topic selected → topic thread; has kids + no topic → empty. */
  displayMessages = computed(() => {
    const topicId = this.selectedTopicId();
    const kidId = this.selectedChildId();
    const pendingThread = this.pendingThreadConversation();
    if (!topicId && pendingThread?.messages?.length) {
      return pendingThread.messages;
    }
    if (topicId) {
      const topic = this.recentTopics().find((t) => t.id === topicId);
      if (kidId) {
        if (topic?.kidId && topic.kidId !== kidId) {
          return [];
        }
      } else if (topic?.kidId?.trim()) {
        return [];
      }
    }
    if (topicId) {
      const q = this.conversationForTopicQuery;
      const cached = q.data()?.messages;
      if (cached?.length) return cached;
      if (q.isPending() && q.data() === undefined) return [];
      return cached ?? [];
    }
    if (this.isVerificationMode()) return this.conversationMessages();
    return [];
  });

  /**
   * Writes POST /conversation response into query cache so the thread updates immediately.
   * Invalidation still runs so lists and server state reconcile; GET can lag behind the POST body.
   */
  applyStartConversationToCache(
    dto: AiGen.MessageDto,
    conversation: AiGen.Conversation,
  ): void {
    if (!conversation?.messages?.length) return;
    if (this.isVerificationMode()) {
      this.#queryClient.setQueryData(
        [...QUERY_KEY_PREFIX, 'conversation', true],
        conversation,
      );
      return;
    }
    const tid = dto.topicId?.trim();
    if (tid) {
      this.seedConversationForTopic(tid, conversation);
    }
  }

  /**
   * Writes POST /conversation into the topic thread cache without dropping prior turns.
   */
  seedConversationForTopic(topicId: string, conversation: AiGen.Conversation): void {
    if (!topicId?.trim() || !conversation?.messages?.length) return;
    const key = [...QUERY_KEY_PREFIX, 'conversation-for-topic', topicId];
    const incoming = conversation.messages ?? [];
    const existing = this.#queryClient.getQueryData<AiGen.Conversation>(key);
    const prior = existing?.messages ?? [];

    if (prior.length === 0 || incoming.length >= prior.length) {
      this.#queryClient.setQueryData(key, { messages: incoming });
      return;
    }

    // POST returned only the latest turn — append to existing thread history.
    this.#queryClient.setQueryData(key, {
      messages: [...prior, ...incoming.slice(-2)],
    });
  }

  /** Invalidate main conversation so it refetches (only relevant in verification mode). */
  invalidateConversation(): void {
    this.#queryClient.invalidateQueries({
      queryKey: [...QUERY_KEY_PREFIX, 'conversation', true],
    });
  }

  /** Clears per-topic conversation cache when switching children. */
  clearConversationCachesForChildChange(): void {
    this.selectedTopicId.set(null);
    this.pendingThreadConversation.set(null);
    this.clearPlanGate();
    this.#queryClient.removeQueries({
      queryKey: [...QUERY_KEY_PREFIX, 'conversation-for-topic'],
    });
  }

  #recentTopicsQueryKey(kidId: string | null) {
    return [...QUERY_KEY_PREFIX, 'topics-recent', kidId ?? 'none'] as const;
  }

  #fetchRecentTopics(kidId: string | null) {
    return lastValueFrom(
      this.#http.get<AiGen.TopicThreadsDto>(`${BASE}/topics/recent`, {
        params: kidId ? { limit: 50, kidId } : { limit: 50 },
      }),
    );
  }

  /**
   * Waits for recent topics for the current scope. Uses fetchQuery so we join the
   * injectQuery request when selectedChildId just changed (avoids duplicate /recent calls).
   */
  async #ensureRecentTopicsLoaded(kidId: string | null): Promise<void> {
    await this.#queryClient.fetchQuery({
      queryKey: this.#recentTopicsQueryKey(kidId),
      queryFn: () => this.#fetchRecentTopics(kidId),
    });
  }

  /**
   * After the selected child changes: load that scope's topics, select the first thread
   * (API order: most recent), and load its conversation. Returns whether a topic exists.
   */
  async syncAfterChildChange(): Promise<boolean> {
    if (this.isVerificationMode()) {
      this.selectedTopicId.set(null);
      return false;
    }

    const kidId = this.selectedChildId();
    this.clearConversationCachesForChildChange();

    await this.#ensureRecentTopicsLoaded(kidId);

    const scoped = topicsForKid(this.recentTopics(), kidId);
    const firstTopic = scoped[0];
    if (firstTopic) {
      this.selectedTopicId.set(firstTopic.id);
      return true;
    }

    this.selectedTopicId.set(null);
    return false;
  }

  /** @deprecated Use {@link syncAfterChildChange}. Kept for header until it calls the async helper. */
  syncTopicToSelectedChild(): void {
    const kidId = this.selectedChildId();
    const forKid = topicsForKid(this.recentTopics(), kidId);
    const firstTopic = forKid[0];
    this.selectedTopicId.set(firstTopic?.id ?? null);
  }

  /**
   * Refetches recent topics and sets selectedTopicId to the newest topic for the given kid.
   * Call after sending the first message in a new conversation so the new topic is selected and its thread is shown.
   * When `seedConversation` is provided, it is written to the topic thread cache before selecting the topic so the
   * for-topic query does not briefly fetch empty data and overwrite the POST response.
   */
  async selectNewestTopicForChild(
    kidId: string | null,
    seedConversation?: ParentAiConversationResponse,
  ): Promise<void> {
    await this.#queryClient.refetchQueries({
      queryKey: this.#recentTopicsQueryKey(kidId),
    });
    const topics = this.recentTopics();
    const forKid = topicsForKid(topics, kidId);
    const latest = [...forKid].sort(
      (a, b) => (b.lastUpdatedDate ?? 0) - (a.lastUpdatedDate ?? 0),
    )[0];
    if (latest) {
      if (seedConversation?.messages?.length) {
        this.seedConversationForTopic(latest.id, seedConversation);
      }
      this.selectedTopicId.set(latest.id);
      this.pendingThreadConversation.set(null);
    }
  }

  /**
   * Refetches the conversation for the given topic (e.g. after sending the first message in a new conversation,
   * so the assistant reply is included once the backend has written it).
   */
  refetchConversationForTopic(topicId: string): void {
    this.#queryClient.invalidateQueries({
      queryKey: [...QUERY_KEY_PREFIX, 'conversation-for-topic', topicId],
    });
  }

  refetchTopicsForChild(kidId: string | null): void {
    void this.#queryClient.refetchQueries({
      queryKey: this.#recentTopicsQueryKey(kidId),
    });
  }

  /**
   * After POST /conversation: follow server-routed topic (pivot / new thread)
   * or keep seeding the topic the parent had selected.
   */
  async applyConversationAfterSend(
    conversation: ParentAiConversationResponse,
    kidId: string | null,
    sentTopicId?: string,
  ): Promise<void> {
    const activeTopicId = conversation.activeTopicId?.trim();
    const sentId = sentTopicId?.trim() ?? '';
    const switched = conversation.topicSwitched === true;

    if (activeTopicId && (switched || !sentId)) {
      if (conversation.messages?.length) {
        this.seedConversationForTopic(activeTopicId, conversation);
      }
      await this.#queryClient.refetchQueries({
        queryKey: this.#recentTopicsQueryKey(kidId),
      });
      this.selectedTopicId.set(activeTopicId);
      this.pendingThreadConversation.set(null);
      return;
    }

    if (sentId) {
      this.seedConversationForTopic(sentId, conversation);
      return;
    }

    await this.selectNewestTopicForChild(kidId, conversation);
  }

  // ─── Topics ────────────────────────────────────────────────────────────────
  // In active mode, fetch recent topics scoped to the selected kid.
  // In verification mode, topic UX is hidden so this query is disabled.

  topicsQuery = injectQuery(() => {
    const kidId = this.selectedChildId();
    const isVerification = this.isVerificationMode();
    return {
      queryKey: this.#recentTopicsQueryKey(kidId),
      queryFn: () => this.#fetchRecentTopics(kidId),
      retry: false,
      enabled: this.#authService.isAuthenticated() && !isVerification,
      // Avoid showing another kid's cached topic list while the scoped fetch runs.
      placeholderData: undefined,
    };
  });

  recentTopicsQuery = this.topicsQuery;

  topicsByCategoryQuery = injectQuery(() => ({
    queryKey: [...QUERY_KEY_PREFIX, 'topics-by-category'],
    queryFn: () =>
      lastValueFrom(
        this.#http.get<AiGen.TopicsByCategoryDto>(`${BASE}/topics/by-category`),
      ),
    retry: false,
    enabled: false,
  }));

  tagCloudQuery = injectQuery(() => ({
    queryKey: [...QUERY_KEY_PREFIX, 'tag-cloud'],
    queryFn: () =>
      lastValueFrom(
        this.#http.get<AiGen.TagCloudDto>(`${BASE}/topics/tag-cloud`),
      ),
    retry: false,
    enabled: false,
  }));

  topicTimelineQuery = injectQuery(() => ({
    queryKey: [...QUERY_KEY_PREFIX, 'topics-timeline'],
    queryFn: () =>
      lastValueFrom(
        this.#http.get<AiGen.TopicThreadsDto>(`${BASE}/topics/timeline`),
      ),
    retry: false,
    enabled: false,
  }));

  recentTopics = computed(() => this.recentTopicsQuery.data()?.topics ?? []);
  topicTimeline = computed(() => this.topicTimelineQuery.data()?.topics ?? []);

  // ─── Mutations ────────────────────────────────────────────────────────────

  sendMessageMutation = injectMutation(() => ({
    mutationFn: (dto: AiGen.MessageDto) =>
      lastValueFrom(
        this.#http.post<ParentAiConversationResponse>(
          `${BASE}/conversation`,
          dto,
        ),
      ),
    onSuccess: (data, dto: AiGen.MessageDto) => {
      this.setPlanGate(data?.planGateCode?.trim() || null);
      this.applyStartConversationToCache(dto, data);
      if (
        !this.isVerificationMode() &&
        !dto.topicId?.trim() &&
        data?.messages?.length
      ) {
        this.pendingThreadConversation.set(data);
      }
      // Refresh topic list only; do not invalidate conversation-for-topic (POST body is already scoped).
      void this.#queryClient.invalidateQueries({
        queryKey: [...QUERY_KEY_PREFIX, 'topics-recent'],
      });
      if (isDutyCreationIntent(dto.message)) {
        this.#queryClient.invalidateQueries({ queryKey: ['user'] });
      }
    },
  }));

  resolveTopicMutation = injectMutation(() => ({
    mutationFn: (dto: AiGen.ResolveTopicRequest) =>
      lastValueFrom(this.#http.post(`${BASE}/topics/resolve`, dto)),
    onSuccess: () => {
      this.#queryClient.invalidateQueries({ queryKey: QUERY_KEY_PREFIX });
    },
  }));

  scribeTokenMutation = injectMutation(() => ({
    mutationFn: () =>
      lastValueFrom(
        this.#http.get<{ token: string }>(`${BASE}/scribe-token`),
      ).then((res) => res?.token ?? ''),
  }));

  recordSpeechUsageMutation = injectMutation(() => ({
    mutationFn: (durationSeconds: number) =>
      lastValueFrom(
        this.#http.post<{ ok: true }>(`${BASE}/speech-usage`, {
          durationSeconds,
        }),
      ),
  }));

  // ─── Derived state ─────────────────────────────────────────────────────────

  isSending = computed(() => this.sendMessageMutation.isPending());

  /** @see resolveSendScope */
  resolveSendScope(
    kidId: string | null,
    topicId: string | null,
  ): { kidId: string; topicId: string } {
    return resolveSendScope(kidId, topicId, this.topics());
  }
  /** General parenting mode: kids linked but no child selected (neutral scope). */
  isNeutralParentingScope = computed(
    () => !this.isVerificationMode() && this.selectedChildId() === null,
  );
  topics = computed(() =>
    topicsForKid(this.topicsQuery.data()?.topics ?? [], this.selectedChildId()),
  );
  topicsByCategory = computed(
    () => this.topicsByCategoryQuery.data()?.items ?? [],
  );
  tagCloud = computed(() => this.tagCloudQuery.data()?.items ?? []);

  /** Last selected linked kid from localStorage, if still in the list. */
  getStoredChildId(linkedKids: readonly { id: string }[]): string | null {
    return this.#family.getStoredChildId(linkedKids);
  }

  isNeutralChildSelectionStored(): boolean {
    return this.#family.isNeutralChildSelectionStored();
  }

  persistSelectedChildId(childId: string | null): void {
    this.#family.persistSelectedChildId(childId);
  }

  armTopicSyncForChild(childId: string): void {
    this.kidAwaitingTopicSync.set(childId);
  }

  armTopicSyncForNeutral(): void {
    this.kidAwaitingTopicSync.set(KID_TOPIC_SYNC_NEUTRAL);
  }

  /** Clears topic selection so the next send starts a new thread. */
  startNewConversation(): void {
    this.selectedTopicId.set(null);
    this.pendingThreadConversation.set(null);
    this.clearPlanGate();
  }

  dismissPlanGate(): void {
    this.clearPlanGate();
  }

  setPlanGate(code: string | null, limit?: number | null): void {
    const trimmed = code?.trim() || null;
    this.planGateCode.set(trimmed);
    this.planGateLimit.set(
      trimmed && typeof limit === 'number' && Number.isFinite(limit)
        ? limit
        : null,
    );
  }

  clearPlanGate(): void {
    this.planGateCode.set(null);
    this.planGateLimit.set(null);
  }

  /** Switch to a linked child (family bar + assistant). */
  selectLinkedChild(childId: string): void {
    if (this.selectedChildId() === childId) {
      return;
    }
    this.#family.selectChild(childId);
    if (this.isVerificationMode()) {
      this.invalidateConversation();
      return;
    }
    this.clearConversationCachesForChildChange();
    this.kidAwaitingTopicSync.set(childId);
  }

  selectNeutralChild(): void {
    if (this.selectedChildId() === null) {
      return;
    }
    this.#family.selectNeutral();
    this.clearConversationCachesForChildChange();
    this.kidAwaitingTopicSync.set(KID_TOPIC_SYNC_NEUTRAL);
  }

  /**
   * Ensures a valid scope: keep current kid, restore stored kid/neutral, or default first child.
   * Returns true when selection was updated (caller may set kidAwaitingTopicSync).
   */
  syncInitialChildSelection(linkedKids: readonly { id: string }[]): boolean {
    if (linkedKids.length === 0) {
      if (!this.isVerificationMode()) {
        return false;
      }
      if (this.selectedChildId() !== null) {
        this.#family.selectNeutral();
      }
      this.kidAwaitingTopicSync.set(null);
      return false;
    }

    const current = this.selectedChildId();
    if (current && linkedKids.some((k) => k.id === current)) {
      return false;
    }

    if (current === null && this.isNeutralChildSelectionStored()) {
      if (
        !this.kidAwaitingTopicSync() &&
        !this.topicSyncInFlight() &&
        this.topicsQuery.data() === undefined
      ) {
        this.kidAwaitingTopicSync.set(KID_TOPIC_SYNC_NEUTRAL);
      }
      return false;
    }

    const storedKid = this.getStoredChildId(linkedKids);
    if (storedKid) {
      this.#family.selectChild(storedKid);
      this.kidAwaitingTopicSync.set(storedKid);
      return true;
    }

    this.#family.selectChild(linkedKids[0]!.id);
    this.kidAwaitingTopicSync.set(linkedKids[0]!.id);
    return true;
  }
}
